// D&D 5e Recharge Types for Items and Abilities
// Based on SRD and DMG rules

import { effectCalculator } from '../services/effectCalculator';

export const RECHARGE_TYPES = {
  NONE: 'none',                  // No recharge (permanent items)
  SHORT_REST: 'short_rest',      // Recharges on short rest (1+ hours)
  LONG_REST: 'long_rest',        // Recharges on long rest (8+ hours)
  DAWN: 'dawn',                  // Recharges at dawn (common for magic items)
  DAILY: 'daily',                // Once per 24 hours
  WEEKLY: 'weekly',              // Once per 7 days
  MONTHLY: 'monthly',            // Once per 30 days
  CONSUMABLE: 'consumable',      // Never recharges (potions, scrolls)
  CUSTOM: 'custom',              // Special conditions (full moon, etc.)
  SPECIAL: 'special'             // Complex recharge (e.g., "1d6 charges at dawn")
};

export const RECHARGE_LABELS = {
  none: 'No Recharge',
  short_rest: 'Short Rest',
  long_rest: 'Long Rest',
  dawn: 'Dawn',
  daily: 'Daily (24 hours)',
  weekly: 'Weekly',
  monthly: 'Monthly',
  consumable: 'Consumable',
  custom: 'Custom',
  special: 'Special'
};

export const RECHARGE_DESCRIPTIONS = {
  none: 'This item does not need to recharge',
  short_rest: 'Recharges after a short rest (1+ hours)',
  long_rest: 'Recharges after a long rest (8+ hours)',
  dawn: 'Recharges at dawn each day',
  daily: 'Can be used once per 24 hours',
  weekly: 'Can be used once per week',
  monthly: 'Can be used once per month',
  consumable: 'Single use item that cannot be recharged',
  custom: 'Recharges under special conditions',
  special: 'Complex recharge mechanic (e.g., rolls dice)'
};

// Class Hit Dice per D&D 5e SRD
export const CLASS_HIT_DICE = {
  'Barbarian': 'd12',
  'Bard': 'd8',
  'Cleric': 'd8',
  'Druid': 'd8',
  'Fighter': 'd10',
  'Monk': 'd8',
  'Paladin': 'd10',
  'Ranger': 'd10',
  'Rogue': 'd8',
  'Sorcerer': 'd6',
  'Warlock': 'd8',
  'Wizard': 'd6',
  // Additional classes
  'Artificer': 'd8',
  'Blood Hunter': 'd10'
};

// Features that recharge on rest
export const REST_FEATURES = {
  // Short Rest
  SHORT_REST: {
    'Action Surge': { class: 'Fighter', level: 2 },
    'Second Wind': { class: 'Fighter', level: 1 },
    'Channel Divinity': { class: 'Cleric', level: 2 },
    'Wild Shape': { class: 'Druid', level: 2 },
    'Ki Points': { class: 'Monk', level: 2 },
    'Bardic Inspiration': { class: 'Bard', level: 5 }, // Level 5+ only
    'Arcane Recovery': { class: 'Wizard', level: 1 },
    'Superiority Dice': { class: 'Fighter', subclass: 'Battle Master' },
    'Pact Magic': { class: 'Warlock', level: 1 }
  },
  
  // Long Rest
  LONG_REST: {
    'Rage': { class: 'Barbarian', level: 1 },
    'Bardic Inspiration': { class: 'Bard', level: 1, maxLevel: 4 }, // Level 1-4
    'Sorcery Points': { class: 'Sorcerer', level: 2 },
    'Lay on Hands': { class: 'Paladin', level: 1 },
    'Divine Sense': { class: 'Paladin', level: 1 },
    'Favored Foe': { class: 'Ranger', level: 1 },
    'Infusions': { class: 'Artificer', level: 2 }
  }
};

// Helper function to determine hit die size from class
export function getHitDieSize(className) {
  const hitDie = CLASS_HIT_DICE[className];
  if (!hitDie) return 'd8'; // Default to d8 if unknown
  return hitDie;
}

// Helper function to get hit die number from string
export function getHitDieNumber(dieString) {
  const match = dieString.match(/d(\d+)/);
  return match ? parseInt(match[1]) : 8;
}

// Calculate total hit dice for multiclass character
export function calculateTotalHitDice(classes) {
  const hitDice = {
    d6: { current: 0, max: 0 },
    d8: { current: 0, max: 0 },
    d10: { current: 0, max: 0 },
    d12: { current: 0, max: 0 }
  };
  
  classes.forEach(classData => {
    const die = getHitDieSize(classData.className);
    const level = classData.level || 1;
    
    hitDice[die].max += level;
    hitDice[die].current += level; // Start with all HD available
  });
  
  return hitDice;
}

// Roll hit dice for healing
// [FIXED 2025-11-11]: Now uses effectCalculator for CON modifier (includes Amulet of Health, feats, temp effects)
// [Phase 4b-infra APR 2026]: Apply Durable feat floor — when registered, the
// minimum HP regained per Hit Die is max(2 × CON mod, 2). PHB p.166.
export function rollHitDice(dieSize, character, inventory = [], activeQuickBonuses = []) {
  const dieNumber = getHitDieNumber(dieSize);
  const roll = Math.floor(Math.random() * dieNumber) + 1;
  const conMod = effectCalculator.getEffectiveModifier(
    character,
    inventory,
    activeQuickBonuses,
    'constitution'
  );
  const baseGain = roll + conMod;
  const durableFloor = effectCalculator.getHitDieRecoveryMinimum(character, inventory);
  // RAW: HP regained per HD spent is at least 1; Durable raises that floor when applicable.
  const total = Math.max(baseGain, durableFloor, 1);

  return {
    roll,
    conMod,
    total,
    durableFloor: durableFloor > 0 ? durableFloor : undefined,
    formula: `1${dieSize} + ${conMod >= 0 ? '+' : ''}${conMod}`
  };
}

// Calculate HP regained on long rest (always full in 5e)
export function calculateLongRestHP(maxHP) {
  return maxHP;
}

// Calculate hit dice regained on long rest (half, minimum 1)
export function calculateHitDiceRegained(maxHitDice) {
  return Math.max(1, Math.floor(maxHitDice / 2));
}

// [TIER 1.3 FIX APR 2026 — Calendar/Rest desync]: canShortRest and
//   canLongRest now take a `currentTime` parameter (in-game wall clock).
//   Pre-fix they called `new Date()` directly, which compared real-world
//   wall time to in-game lastLongRest values produced by long rests.
//   The diff went negative-forever and the rest buttons stayed disabled
//   after a single rest. Callers must pass timeSystem.currentTime — the
//   parameter is required at the call site even though it has a default
//   here (defensive: `new Date()` keeps the helper usable in pure-test
//   contexts that haven't wired up a clock, but the production code
//   path always supplies the in-game clock value).
//
// Check if enough time has passed for rest
// [TIER 1.3 FIX APR 2026]: AND-logic between short and long. Pre-fix was
//   `timeSinceShort >= 1 || timeSinceLong >= 1`, which is permissive when
//   one of them is null (Infinity short-circuits the OR). Correct
//   behavior: short rest is allowed if BOTH the last short rest AND the
//   last long rest are at least 1h ago. Null-side is treated as
//   "infinitely long ago" — i.e., automatically passes its own gate.
export function canShortRest(lastShortRest, lastLongRest, currentTime = new Date()) {
  if (!lastShortRest && !lastLongRest) return true;

  const now = new Date(currentTime);
  const timeSinceShort = lastShortRest ? (now - new Date(lastShortRest)) / (1000 * 60 * 60) : Infinity;
  const timeSinceLong = lastLongRest ? (now - new Date(lastLongRest)) / (1000 * 60 * 60) : Infinity;

  // Can short rest if 1 hour has passed since BOTH the last short rest
  // and the last long rest. Null-side passes automatically (Infinity).
  return timeSinceShort >= 1 && timeSinceLong >= 1;
}

export function canLongRest(lastLongRest, currentTime = new Date()) {
  if (!lastLongRest) return true;

  const now = new Date(currentTime);
  const timeSince = (now - new Date(lastLongRest)) / (1000 * 60 * 60);

  // Can long rest if 24 hours have passed (once per day)
  return timeSince >= 24;
}

// Calculate dawn times
export function getNextDawn(currentTime) {
  const dawn = new Date(currentTime);
  dawn.setHours(6, 0, 0, 0);
  
  // If it's already past dawn today, get tomorrow's dawn
  if (currentTime >= dawn) {
    dawn.setDate(dawn.getDate() + 1);
  }
  
  return dawn;
}

export function isDawnCrossed(lastTime, currentTime) {
  const lastDawn = new Date(lastTime);
  lastDawn.setHours(6, 0, 0, 0);
  
  const currentDawn = new Date(currentTime);
  currentDawn.setHours(6, 0, 0, 0);
  
  // Check if we crossed dawn between last time and current time
  return lastTime < lastDawn && currentTime >= currentDawn;
}

// Recharge calculations for items
export function calculateRecharge(rechargeType, rechargeAmount, lastRecharged, currentTime) {
  switch (rechargeType) {
    case RECHARGE_TYPES.DAWN:
      return isDawnCrossed(lastRecharged, currentTime);
      
    case RECHARGE_TYPES.DAILY:
      const daysSince = (currentTime - lastRecharged) / (1000 * 60 * 60 * 24);
      return daysSince >= 1;
      
    case RECHARGE_TYPES.WEEKLY:
      const weeksSince = (currentTime - lastRecharged) / (1000 * 60 * 60 * 24 * 7);
      return weeksSince >= 1;
      
    case RECHARGE_TYPES.MONTHLY:
      const monthsSince = (currentTime - lastRecharged) / (1000 * 60 * 60 * 24 * 30);
      return monthsSince >= 1;
      
    case RECHARGE_TYPES.SHORT_REST:
    case RECHARGE_TYPES.LONG_REST:
      return true; // Handled by rest functions
      
    default:
      return false;
  }
}

// Roll for variable recharge amounts (e.g., "1d6+1")
export function rollRechargeAmount(rechargeFormula) {
  if (!rechargeFormula) return 0;
  
  // Handle fixed numbers
  if (!isNaN(rechargeFormula)) {
    return parseInt(rechargeFormula);
  }
  
  // Handle dice notation (e.g., "1d6+1")
  const match = rechargeFormula.match(/(\d+)d(\d+)([+-]\d+)?/);
  if (match) {
    const numDice = parseInt(match[1]);
    const dieSize = parseInt(match[2]);
    const modifier = match[3] ? parseInt(match[3]) : 0;
    
    let total = modifier;
    for (let i = 0; i < numDice; i++) {
      total += Math.floor(Math.random() * dieSize) + 1;
    }
    
    return Math.max(0, total);
  }
  
  return 0;
}

// Common magic item recharge patterns
export const COMMON_RECHARGES = {
  'Wand': { type: RECHARGE_TYPES.DAWN, amount: '1d6+1' },
  'Staff': { type: RECHARGE_TYPES.DAWN, amount: '1d6+1' },
  'Rod': { type: RECHARGE_TYPES.DAWN, amount: '1d4+1' },
  'Ring': { type: RECHARGE_TYPES.DAWN, amount: '1d3' },
  'Potion': { type: RECHARGE_TYPES.CONSUMABLE, amount: null },
  'Scroll': { type: RECHARGE_TYPES.CONSUMABLE, amount: null }
};

export default {
  RECHARGE_TYPES,
  RECHARGE_LABELS,
  RECHARGE_DESCRIPTIONS,
  CLASS_HIT_DICE,
  REST_FEATURES,
  COMMON_RECHARGES,
  getHitDieSize,
  getHitDieNumber,
  calculateTotalHitDice,
  rollHitDice,
  calculateLongRestHP,
  calculateHitDiceRegained,
  canShortRest,
  canLongRest,
  getNextDawn,
  isDawnCrossed,
  calculateRecharge,
  rollRechargeAmount
};